import { useEffect, useMemo, useState } from 'react';
import { CoreBox, CoreChip, CoreTypography } from '@youscience/core';
import { FormControl, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material';
import { ScholarshipStatus } from '@common-types';

import ActionButton from '@components/ActionButton';

import useApplyingScholarshipsStore from '@store/ApplyingScholarshipsStore';
import useFavoriteScholarshipsStore from '@store/FavoriteScholarshipsStore';
import useEducationPlannerStore from '@store/EducationPlannerStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { FILTER_TAGS_COLORS } from '@constants/applyingScholarships';

import EditIcon from '@images/pencil.svg?react';
import CancelIcon from '@images/delete-checkpoint.svg?react';
import SaveIcon from '@images/mark.svg?react';
import UrlIcon from '@images/url.svg?react';
import HeartIcon from '@images/heart.svg?react';
import FilledHeartIcon from '@images/heart-filled.svg?react';
import YesOptionIcon from '@images/yes-option.svg?react';
import NoOptionIcon from '@images/no-option.svg?react';

import {
  sxStyles,
  StyledHeadSectionActions,
  StyledApplyStepBtn,
  StyledActionIcon,
  StyledWebsiteLink,
} from './HeaderActions.styles';

interface HeaderActionsProps {
  scholarshipId: number;
  webAddress?: string;
  onApplyScholarship: () => void;
}

export const HeaderActions = (props: HeaderActionsProps) => {
  const { scholarshipId, webAddress = '', onApplyScholarship } = props;

  const [isApplyStepActive, setIsApplyStepActive] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { applyingScholarships } = useEducationPlannerStore((state) => state.userPlanning);
  const scholarshipFavorites = useUserPreferencesStore((state) => state.scholarshipFavorites);

  const toggleFavorite = useFavoriteScholarshipsStore((state) => state.toggleFavorite);
  const changeApplyStatus = useApplyingScholarshipsStore((state) => state.changeApplyStatus);
  const applyScholarship = useApplyingScholarshipsStore((state) => state.applyScholarship);

  const initialApplyStatus = useMemo(
    () =>
      applyingScholarships?.find((item) => item.id === +scholarshipId)?.applicationStatus || ScholarshipStatus.CHANGED,
    [applyingScholarships, scholarshipId],
  );

  const [currentApplyStatus, setCurrentApplyStatus] = useState<ScholarshipStatus>(initialApplyStatus);

  const scholarshipWebsite = webAddress.match(/^[a-zA-Z]+:\/\//) ? webAddress : `https://${webAddress}`;

  const rootWebsiteLink =
    scholarshipWebsite.match(/^(?:https?:)?(?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n]+)/im)?.[0] || scholarshipWebsite;

  const isFavoriteScholarship = useMemo(() => {
    return !!scholarshipFavorites?.find((item) => item.id === +scholarshipId);
  }, [scholarshipFavorites, scholarshipId]);

  const handleToggleFavorite = () => {
    void toggleFavorite(+scholarshipId, isFavoriteScholarship);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    setCurrentApplyStatus(event.target.value as ScholarshipStatus);
  };

  const handleOnApplyStep = () => {
    setIsApplyStepActive(true);
    onApplyScholarship();
  };

  const handleOffApplyStep = () => {
    setIsApplyStepActive(false);
  };

  const handleOnEditMode = () => setIsEditMode(true);

  const handleOffEditMode = () => {
    setCurrentApplyStatus(initialApplyStatus);
    setIsEditMode(false);
  };

  const handleSaveApplyStatus = () => {
    if (initialApplyStatus !== currentApplyStatus) {
      void changeApplyStatus(scholarshipId, currentApplyStatus);
    }

    setIsEditMode(false);
  };

  const handleApplyScholarship = () => {
    setCurrentApplyStatus(ScholarshipStatus.IN_PROGRESS);

    void applyScholarship(+scholarshipId);

    handleOffApplyStep();
  };

  useEffect(() => {
    setCurrentApplyStatus(initialApplyStatus);
  }, [initialApplyStatus]);

  if (!webAddress) return null;

  return (
    <StyledHeadSectionActions>
      {isApplyStepActive ? (
        <CoreBox sx={sxStyles.applyStepWrap}>
          <CoreTypography>Did you start your application?</CoreTypography>

          <StyledApplyStepBtn onClick={handleApplyScholarship}>
            <YesOptionIcon />

            <CoreTypography>Yes</CoreTypography>
          </StyledApplyStepBtn>

          <StyledApplyStepBtn onClick={handleOffApplyStep}>
            <NoOptionIcon />

            <CoreTypography>No</CoreTypography>
          </StyledApplyStepBtn>
        </CoreBox>
      ) : (
        <>
          {initialApplyStatus !== ScholarshipStatus.CHANGED ? (
            <CoreBox sx={sxStyles.wrap}>
              {isEditMode ? (
                <>
                  <FormControl size="small" sx={sxStyles.subHeaderSelectWrap}>
                    <InputLabel>Admission status</InputLabel>

                    <Select
                      data-testid="admissionStatusSelect"
                      value={currentApplyStatus}
                      onChange={handleSelectChange}
                      input={<OutlinedInput label="Application status" />}
                      renderValue={(selected) => (
                        <CoreChip
                          key={selected}
                          sx={{
                            color: FILTER_TAGS_COLORS[currentApplyStatus],
                          }}
                          label={selected}
                          variant="outlined"
                        />
                      )}
                    >
                      {Object.keys(ScholarshipStatus).map((key) => (
                        <MenuItem key={key} value={ScholarshipStatus[key as keyof typeof ScholarshipStatus]}>
                          {(ScholarshipStatus as Record<string, unknown>)[key] as ScholarshipStatus}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <StyledActionIcon onClick={handleSaveApplyStatus}>
                    <SaveIcon />
                  </StyledActionIcon>

                  <StyledActionIcon onClick={handleOffEditMode}>
                    <CancelIcon />
                  </StyledActionIcon>
                </>
              ) : (
                <>
                  <CoreChip
                    sx={{ color: FILTER_TAGS_COLORS[currentApplyStatus] }}
                    label={currentApplyStatus}
                    variant="outlined"
                    size="medium"
                  />

                  <StyledActionIcon onClick={handleOnEditMode}>
                    <EditIcon />
                  </StyledActionIcon>
                </>
              )}
            </CoreBox>
          ) : (
            <ActionButton sx={sxStyles.applyBtn} onClick={handleOnApplyStep}>
              Apply
            </ActionButton>
          )}

          <ActionButton
            data-testid={`${isFavoriteScholarship ? 'removeFrom' : 'addTo'}FavoritesButton`}
            onClick={handleToggleFavorite}
            startIcon={isFavoriteScholarship ? <FilledHeartIcon /> : <HeartIcon />}
          >
            {isFavoriteScholarship ? 'Remove from favorites' : 'Add to favorites'}
          </ActionButton>

          {webAddress && (
            <StyledWebsiteLink target="_blank" href={scholarshipWebsite}>
              <UrlIcon />

              {rootWebsiteLink}
            </StyledWebsiteLink>
          )}
        </>
      )}
    </StyledHeadSectionActions>
  );
};
